import { isEmpty, isFunction } from 'lodash';
import { XCircle } from 'lucide-react';
import { forwardRef, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover';
import { ComboboxTagsTrigger } from '@/components/ui/inputs/combobox-tags/trigger';
import { Command, CommandInput, CommandItem, CommandList } from '@/components/ui/inputs/command';
import { TagModel } from '@/lib/models/tag/types';
import { tv } from '@/utils/styles';

export interface ComboboxTagsProps {
  /**
   * An array of OptionGroup objects to be displayed in the multi-select component.
   */
  options: TagModel[];

  /**
   * Callback function triggered when a tag is added.
   * Receives a string of the added value
   */
  onAddTag: (value: string) => void;

  /**
   * Callback function triggered when a tag is removed.
   * Receives a string of the removed value
   */
  onRemoveTag: (value: string) => void;

  /**
   * Callback function triggered when the tags modal is dismissed
   */
  onModalClose: () => void;

  /** The currently selected values */
  selectedValues: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * Callback function triggered when a user clicks create.
   * Optional, will not render create item if undefined
   */
  onCreateNewItem?: (value: string) => void;

  /**
   * Initial value for isPopoverOpen
   * Optional, default to false
   */
  isOpen?: boolean;

  /**
   * Initial value for searchValue
   * Optional, defaults to empty string
   */
  searchValue?: string;
}

const ComboboxTags = forwardRef<HTMLButtonElement, ComboboxTagsProps>(
  (
    {
      options,
      isOpen,
      onCreateNewItem,
      className,
      placeholder = 'Add tag',
      searchValue: searchValueProp = '',
      selectedValues,
      onAddTag,
      onRemoveTag,
      modalPopover,
      onModalClose,
    },
    ref,
  ) => {
    const [searchValue, setSearchValue] = useState(searchValueProp);
    const [isPopoverOpen, setIsPopoverOpen] = useState(isOpen);

    const filteredOptions = options.filter((option) => !selectedValues.includes(option.label));

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const toggleOption = (value: string) => {
      return selectedValues.includes(value) ? onRemoveTag(value) : onAddTag(value);
    };

    const handleCreateAndSelectNewItem = (value: string) => {
      if (isFunction(onCreateNewItem)) {
        onCreateNewItem(value);
        setSearchValue('');
        toggleOption(value);
      }
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        const value = newSelectedValues.pop();
        if (value) onRemoveTag(value);
      }
    };

    const handleOpenChange = (value: boolean) => {
      setIsPopoverOpen(value);
      if (!value) {
        onModalClose();
      }
    };

    const { popoverContent, commandItem, selectedItemsWrapper, selectedTag, removeIcon, emptyList } = styles();

    return (
      <Popover open={isPopoverOpen} onOpenChange={handleOpenChange} modal={modalPopover}>
        <PopoverTrigger asChild>
          <ComboboxTagsTrigger
            selectedValues={selectedValues}
            onTogglePopover={handleTogglePopover}
            placeholder={placeholder}
            ref={ref}
            isOpen={isPopoverOpen}
            className={className}
          />
        </PopoverTrigger>
        <PopoverContent
          className={popoverContent()}
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
          onClick={(e) => e.stopPropagation()}
        >
          {selectedValues.length ? (
            <div className={selectedItemsWrapper()}>
              {selectedValues.map((selectedValue, i) => {
                return (
                  <div key={i} className={selectedTag()}>
                    <button
                      className="size-3.5"
                      type="button"
                      onClick={() => {
                        toggleOption(selectedValue);
                      }}
                      aria-label={`Remove ${selectedValue}`}
                    >
                      <XCircle size={14} className={removeIcon()} />
                    </button>
                    {selectedValue}
                  </div>
                );
              })}
            </div>
          ) : null}
          <Command className="border-none p-2 shadow-none">
            <CommandInput
              placeholder="Search for a tag"
              onKeyDown={handleInputKeyDown}
              value={searchValue}
              onValueChange={setSearchValue}
              wrapperClassName="rounded-md border border-grey-300 px-2"
              className="h-8"
            />
            <CommandList>
              {filteredOptions.map((option) => {
                return (
                  <CommandItem
                    key={option.id}
                    onSelect={() => toggleOption(option.label)}
                    className={commandItem()}
                    value={option.label}
                  >
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
              {searchValue && (
                <CommandItem
                  key="create"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onSelect={() => handleCreateAndSelectNewItem(searchValue)}
                  className={commandItem()}
                >
                  Create &quot;{searchValue}&quot;
                </CommandItem>
              )}
              {isEmpty(options) && !searchValue && (
                <div className={emptyList()}>
                  No tags have been created yet.
                  <br />
                  To create a tag, type it into the field above and press <em>Enter</em>
                </div>
              )}
              {!isEmpty(options) && isEmpty(filteredOptions) && !searchValue && (
                <div className={emptyList()}>
                  All tags are already selected.
                  <br />
                  To create a new tag, type it into the field above and press <em>Enter</em>
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

ComboboxTags.displayName = 'ComboboxTags';

const styles = tv({
  slots: {
    popoverContent: 'flex w-72 flex-col rounded-md bg-white p-0',
    commandItem: 'flex h-7 cursor-pointer gap-2 rounded-full px-2 first-of-type:mt-2',
    selectedItemsWrapper: 'flex flex-wrap gap-1 rounded-t-md border-b border-grey-200 bg-grey-50 p-2',
    selectedTag: 'flex h-6 items-center gap-1 rounded-full bg-grey-200 p-1.5 text-baseSm leading-none',
    removeIcon: 'hover:text-errorDark',
    emptyList:
      'mt-2 rounded-md border border-dashed border-grey-400 bg-grey-50 px-4 py-3 text-baseSm leading-snug text-grey-600 [&_em]:rounded [&_em]:bg-white [&_em]:px-1 [&_em]:shadow-sm',
  },
});

export { ComboboxTags };
